import { lazy, type ComponentType, type LazyExoticComponent } from 'react'

import { importWithStaleChunkReload } from '@/lib/importWithStaleChunkReload'

/**
 * Single source of truth: one entry per routed page. Each raw dynamic import is
 * surfaced two ways from a single registry — no parallel lists to drift:
 *
 * - Router `lazy()`: wrapped in `importWithStaleChunkReload` so a stale tab that
 *   hits a chunk pruned by a new deploy reloads once instead of erroring.
 * - Idle prefetch: uses the RAW import fn. Prefetch runs without user intent, so
 *   a wrapped failure would surprise-reload the tab bypassing the
 *   `hasUnsavedChanges` gate that AppFreshnessGuard respects. On a failed
 *   prefetch, `usePrefetchPages`'s existing `.catch(err => logger.warn(...))`
 *   swallows the error and the router's own `lazy()` path will reload cleanly
 *   later if the user actually navigates there.
 */
const PAGE_IMPORTS = {
  Login: () => import('@/pages/Login'),
  AcceptTermsOfService: () => import('@/pages/AcceptTermsOfService'),
  Onboarding: () => import('@/pages/Onboarding'),
  TermsOfService: () => import('@/pages/TermsOfService'),
  CustomerSupport: () => import('@/pages/CustomerSupport'),
  OrganizationInvite: () => import('@/pages/OrganizationInvite'),
  Invite: () => import('@/pages/Invite'),
  ScenarioLibrary: () => import('@/pages/ScenarioLibrary'),
  CreateScenario: () => import('@/pages/CreateScenario'),
  EditExistingScenario: () => import('@/pages/EditExistingScenario'),
  ScenarioPlayback: () => import('@/pages/Simulation'),
  B2bHome: () => import('@/pages/B2bHome'),
  B2bWorkspaceWorkflows: () => import('@/pages/B2bWorkspaceWorkflows'),
  B2bWorkflow: () => import('@/pages/B2bWorkflow'),
  B2bSimulation: () => import('@/pages/B2bSimulation'),
  B2bStaffConsole: () => import('@/pages/B2bStaffConsole'),
  B2bNotFound: () => import('@/pages/B2bNotFound'),
  B2bWorkspaceMembers: () => import('@/pages/B2bWorkspaceMembers'),
  B2bWorkspaceSetup: () => import('@/pages/B2bWorkspaceSetup'),
  B2bInvite: () => import('@/pages/B2bInvite'),
  B2bWorkspaceInvitation: () => import('@/pages/B2bWorkspaceInvitation'),
  SessionFeedback: () => import('@/pages/SessionFeedback'),
  RegenerateFeedback: () => import('@/pages/RegenerateFeedback'),
  SessionSignals: () => import('@/pages/SessionSignals'),
  SessionKeyMoments: () => import('@/pages/SessionKeyMoments'),
  WorkspaceSettings: () => import('@/pages/WorkspaceSettings'),
  WorkspaceMembers: () => import('@/pages/WorkspaceMembers'),
  Analytics: () => import('@/pages/Analytics'),
  MyPath: () => import('@/pages/MyPath'),
  MyPathDebug: () => import('@/pages/MyPathDebug'),
  CloneWorkspace: () => import('@/pages/CloneWorkspace'),
  ApiDocs: () => import('@/pages/ApiDocs'),
  AccountSettings: () => import('@/pages/AccountSettings'),
  InviteLinks: () => import('@/pages/InviteLinks'),
}

type PageName = keyof typeof PAGE_IMPORTS

/**
 * Pages deliberately NOT warmed during idle prefetch: heavy (ApiDocs is a ~1.25 MB
 * chunk) or rarely/transiently visited (debug + one-shot token-consume screens).
 * Everything else in PAGE_IMPORTS is prefetched.
 */
const PREFETCH_EXCLUDED: ReadonlySet<PageName> = new Set<PageName>([
  'Invite',
  'RegenerateFeedback',
  'SessionKeyMoments',
  'MyPathDebug',
  'ApiDocs',
  // The B2B portal's pages: only NoPressure B2B people open them, mostly from their agent's link.
  'B2bHome',
  'B2bWorkspaceWorkflows',
  'B2bWorkflow',
  'B2bSimulation',
  'B2bNotFound',
  'B2bWorkspaceMembers',
  'B2bWorkspaceSetup',
  'B2bInvite',
  'B2bWorkspaceInvitation',
  // Only NoPressure staff open it.
  'B2bStaffConsole',
])

// One pass over the registry: router gets the reload-wrapped import (for a
// user-driven navigation to a pruned chunk); prefetch gets the raw import (a
// background failure must not surprise-reload the tab).
const lazyPages = {} as Record<PageName, LazyExoticComponent<ComponentType>>
const prefetchImports: Array<() => Promise<unknown>> = []

for (const name of Object.keys(PAGE_IMPORTS) as PageName[]) {
  const rawImport = PAGE_IMPORTS[name]
  lazyPages[name] = lazy(importWithStaleChunkReload(rawImport))
  if (!PREFETCH_EXCLUDED.has(name)) prefetchImports.push(rawImport)
}

/** Lazy page components for the router — rendered as `Pages.Login`, `Pages.ScenarioLibrary`, … */
export const Pages = lazyPages

/** Raw (unwrapped) import fns warmed by usePrefetchPages during idle time (minus PREFETCH_EXCLUDED). */
export const pageImportFunctions = prefetchImports
